import { useEffect, useRef, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import './Sidebar.scss';
import logoMW from '../../assets/images/MW-logo_thumbnail.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faClipboardCheck,
  faClose,
  faEnvelope,
  faHome,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import {
  faDiscord,
  faGithub,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';

// `end` on the home route stops it matching every path. In react-router v6
// this replaces the v5 `exact` prop; v6 also adds the `active` class itself,
// so the old `activeclassname` prop is gone too — both were being passed
// straight through to the DOM as unknown attributes.
const navLinks = [
  { to: '/', label: 'Home', icon: faHome, end: true },
  { to: '/about', label: 'About Me', icon: faUser },
  { to: '/projects', label: 'Projects', icon: faClipboardCheck },
  { to: '/contact', label: 'Contact', icon: faEnvelope },
];

const socialLinks = [
  {
    href: 'https://www.linkedin.com/in/matthew-ev-walker/',
    label: 'LinkedIn',
    icon: faLinkedin,
  },
  {
    href: 'https://github.com/MatthewEvWalker/',
    label: 'GitHub',
    icon: faGithub,
  },
  {
    href: 'https://discord.gg/BhDY8FMBZP',
    label: 'Discord',
    icon: faDiscord,
  },
];

const Sidebar = () => {
  const [showNav, setShowNav] = useState(false);
  const navRef = useRef(null);
  const openButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!showNav) return undefined;

    const getFocusable = () =>
      Array.from(
        navRef.current?.querySelectorAll('a[href], button:not([disabled])') ?? []
      ).filter((el) => el.offsetParent !== null);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowNav(false);
        openButtonRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      // Keep Tab inside the open panel instead of letting it wander onto the
      // page behind it.
      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // `.page` is the scroll container (see Layout.scss), not <body> — locking
    // the body would do nothing here.
    const scroller = document.querySelector('.page');
    const previousOverflow = scroller?.style.overflowY;
    if (scroller) scroller.style.overflowY = 'hidden';

    // Move focus into the panel so the next Tab starts inside it.
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (scroller) scroller.style.overflowY = previousOverflow ?? '';
    };
  }, [showNav]);

  const handleClose = () => {
    setShowNav(false);
    openButtonRef.current?.focus(); // return focus to whatever opened the menu
  };

  return (
    <div className="nav-bar">
      <Link className="logo" to="/">
        <img src={logoMW} alt="Matthew Walker — home" />
      </Link>

      <button
        type="button"
        className="hamburger-button"
        aria-label="Open navigation menu"
        aria-expanded={showNav}
        aria-controls="main-nav"
        onClick={() => setShowNav(true)}
        ref={openButtonRef}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>

      <nav
        id="main-nav"
        aria-label="Main"
        className={showNav ? 'mobile-show' : ''}
        ref={navRef}
      >
        <button
          type="button"
          className="close-button"
          aria-label="Close navigation menu"
          onClick={handleClose}
          ref={closeButtonRef}
        >
          <FontAwesomeIcon icon={faClose} />
        </button>

        {navLinks.map(({ to, label, icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setShowNav(false)}>
            <FontAwesomeIcon icon={icon} />
            {/* Real text, not a CSS ::after. Generated content is not
                reliably exposed to screen readers, and the icon is
                aria-hidden, so these links previously had no accessible
                name at all. */}
            <span className="nav-label">{label}</span>
          </NavLink>
        ))}
      </nav>

      <ul className="social-links">
        {socialLinks.map(({ href, label, icon }) => (
          <li key={label}>
            <a href={href} target="_blank" rel="noreferrer" aria-label={label}>
              <FontAwesomeIcon icon={icon} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Sidebar;
